import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Search, Archive, Filter, ArrowUpDown } from 'lucide-react';

interface Auftrag {
  id: string;
  auftragsnummer: string;
  internal_number: string;
  kostenstelle?: string;
  sap_nummer?: string;
  auftragsbezeichnung: string;
  auftragsdatum: string;
  monteur_id: string;
  kunde_id: string;
  standort_id: string;
  status: 'neu' | 'begonnen' | 'abgeschlossen' | 'abgerechnet';
  begonnen_am: string | null;
  abgeschlossen_am: string | null;
  abgerechnet_am: string | null;
  pdf_name?: string;
  pdf_data?: string;
}

interface Monteur {
  id: string;
  name: string;
}

interface Kunde {
  id: string;
  name: string;
}

interface Standort {
  id: string;
  name: string;
  adresse: string;
}

type SortField = 'auftragsdatum' | 'internal_number' | 'auftragsnummer' | 'kunde' | 'standort';
type SortDirection = 'asc' | 'desc';

function AuftragsUebersicht() {
  const navigate = useNavigate();
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [monteure, setMonteure] = useState<Monteur[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'alle' | 'neu' | 'begonnen' | 'abgeschlossen' | 'abgerechnet'>('alle');
  const [sortField, setSortField] = useState<SortField>('auftragsdatum');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      
      const [auftraegeRes, kundenRes, standorteRes, monteureRes] = await Promise.all([
        supabase.from('auftraege').select('*'),
        supabase.from('kunden').select('id, name'),
        supabase.from('standorte').select('id, name, adresse'),
        supabase.from('monteure').select('id, name')
      ]);

      if (auftraegeRes.error) throw auftraegeRes.error;
      if (kundenRes.error) throw kundenRes.error;
      if (standorteRes.error) throw standorteRes.error;
      if (monteureRes.error) throw monteureRes.error;

      setAuftraege(auftraegeRes.data || []);
      setKunden(kundenRes.data || []);
      setStandorte(standorteRes.data || []);
      setMonteure(monteureRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      alert('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }

  const getStatusColor = (status: Auftrag['status']) => {
    switch (status) {
      case 'neu':
        return 'bg-blue-100 text-blue-800';
      case 'begonnen':
        return 'bg-yellow-100 text-yellow-800';
      case 'abgeschlossen':
        return 'bg-green-100 text-green-800';
      case 'abgerechnet':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: Auftrag['status']) => {
    switch (status) {
      case 'neu':
        return 'Neu';
      case 'begonnen':
        return 'In Bearbeitung';
      case 'abgeschlossen':
        return 'Abgeschlossen';
      case 'abgerechnet':
        return 'Abgerechnet';
      default:
        return status;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortAuftraege = (auftraege: Auftrag[]) => {
    return [...auftraege].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'auftragsdatum':
          comparison = new Date(a.auftragsdatum).getTime() - new Date(b.auftragsdatum).getTime();
          break;
        case 'internal_number':
          comparison = a.internal_number.localeCompare(b.internal_number);
          break;
        case 'auftragsnummer':
          comparison = a.auftragsnummer.localeCompare(b.auftragsnummer);
          break;
        case 'kunde':
          const kundeA = kunden.find(k => k.id === a.kunde_id)?.name || '';
          const kundeB = kunden.find(k => k.id === b.kunde_id)?.name || '';
          comparison = kundeA.localeCompare(kundeB);
          break;
        case 'standort':
          const standortA = standorte.find(s => s.id === a.standort_id)?.name || '';
          const standortB = standorte.find(s => s.id === b.standort_id)?.name || '';
          comparison = standortA.localeCompare(standortB);
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const openAuftrag = (auftragId: string) => {
    navigate(`/bearbeitung/${auftragId}`);
  };

  const filteredAuftraege = auftraege.filter(auftrag => {
    const statusMatch = filter === 'alle' || auftrag.status === filter;
    const archiveMatch = showArchived || auftrag.status !== 'abgerechnet';
    const searchMatch = searchTerm === '' || 
      auftrag.internal_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      auftrag.auftragsnummer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      auftrag.auftragsbezeichnung.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kunden.find(k => k.id === auftrag.kunde_id)?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      monteure.find(m => m.id === auftrag.monteur_id)?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      standorte.find(s => s.id === auftrag.standort_id)?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      standorte.find(s => s.id === auftrag.standort_id)?.adresse.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (auftrag.kostenstelle && auftrag.kostenstelle.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (auftrag.sap_nummer && auftrag.sap_nummer.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return statusMatch && archiveMatch && searchMatch;
  });

  const sortedAuftraege = sortAuftraege(filteredAuftraege);

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Auftragsübersicht</h1>
        
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('alle')}
              className={`px-4 py-2 rounded-md ${
                filter === 'alle'
                  ? 'bg-gray-200 text-gray-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Alle
            </button>
            <button
              onClick={() => setFilter('neu')}
              className={`px-4 py-2 rounded-md ${
                filter === 'neu'
                  ? 'bg-blue-200 text-blue-800'
                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
              }`}
            >
              Neu
            </button>
            <button
              onClick={() => setFilter('begonnen')}
              className={`px-4 py-2 rounded-md ${
                filter === 'begonnen'
                  ? 'bg-yellow-200 text-yellow-800'
                  : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
              }`}
            >
              In Bearbeitung
            </button>
            <button
              onClick={() => setFilter('abgeschlossen')}
              className={`px-4 py-2 rounded-md ${
                filter === 'abgeschlossen'
                  ? 'bg-green-200 text-green-800'
                  : 'bg-green-100 text-green-600 hover:bg-green-200'
              }`}
            >
              Abgeschlossen
            </button>
            <button
              onClick={() => setFilter('abgerechnet')}
              className={`px-4 py-2 rounded-md ${
                filter === 'abgerechnet'
                  ? 'bg-purple-200 text-purple-800'
                  : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
              }`}
            >
              Abgerechnet
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => handleSort('auftragsdatum')}
              className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                sortField === 'auftragsdatum' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
              }`}
            >
              Datum
              <ArrowUpDown size={16} className={sortField === 'auftragsdatum' ? 'opacity-100' : 'opacity-50'} />
            </button>
            <button
              onClick={() => handleSort('internal_number')}
              className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                sortField === 'internal_number' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
              }`}
            >
              Bestellnummer
              <ArrowUpDown size={16} className={sortField === 'internal_number' ? 'opacity-100' : 'opacity-50'} />
            </button>
            <button
              onClick={() => handleSort('auftragsnummer')}
              className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                sortField === 'auftragsnummer' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
              }`}
            >
              Auftragsnummer
              <ArrowUpDown size={16} className={sortField === 'auftragsnummer' ? 'opacity-100' : 'opacity-50'} />
            </button>
            <button
              onClick={() => handleSort('kunde')}
              className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                sortField === 'kunde' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
              }`}
            >
              Kunde
              <ArrowUpDown size={16} className={sortField === 'kunde' ? 'opacity-100' : 'opacity-50'} />
            </button>
            <button
              onClick={() => handleSort('standort')}
              className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                sortField === 'standort' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
              }`}
            >
              Standort
              <ArrowUpDown size={16} className={sortField === 'standort' ? 'opacity-100' : 'opacity-50'} />
            </button>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Suche nach Bestellnummer, Auftragsnummer, Kunde, Monteur..."
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                  showArchived ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Archive size={18} />
                {showArchived ? 'Archiv ausblenden' : 'Archiv anzeigen'}
              </button>
              <div className="text-sm text-gray-500">
                {!showArchived && (
                  <span className="italic">Abgerechnete Aufträge werden ausgeblendet</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Filter size={14} />
            <span>
              Sortierung: {
                sortField === 'internal_number' ? 'Bestellnummer' :
                sortField === 'auftragsnummer' ? 'Auftragsnummer' :
                sortField === 'auftragsdatum' ? 'Datum' :
                sortField === 'kunde' ? 'Kunde' :
                'Standort'
              } ({sortDirection === 'asc' ? 'aufsteigend' : 'absteigend'})
            </span>
            {searchTerm && (
              <span className="ml-2">
                | Suche: "{searchTerm}"
              </span>
            )}
            <span className="ml-2">
              | {sortedAuftraege.length} Aufträge gefunden
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bestellnummer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auftragsnummer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standort</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedAuftraege.map((auftrag) => {
                const kunde = kunden.find(k => k.id === auftrag.kunde_id);
                const standort = standorte.find(s => s.id === auftrag.standort_id);
                return (
                  <tr 
                    key={auftrag.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${
                      auftrag.status === 'abgerechnet' ? 'bg-purple-50/30' : ''
                    }`}
                    onClick={() => openAuftrag(auftrag.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{auftrag.internal_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{auftrag.auftragsnummer}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{kunde?.name || 'Unbekannt'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{standort?.name || 'Unbekannt'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{new Date(auftrag.auftragsdatum).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(auftrag.status)}`}>
                        {getStatusLabel(auftrag.status)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {sortedAuftraege.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? (
                <div>
                  <p>Keine Aufträge gefunden, die dem Suchbegriff "{searchTerm}" entsprechen</p>
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="mt-2 text-blue-600 hover:text-blue-800"
                  >
                    Suche zurücksetzen
                  </button>
                </div>
              ) : !showArchived ? (
                <div>
                  <p>Keine Aufträge gefunden</p>
                  <p className="text-sm mt-2">
                    Möglicherweise befinden sich Aufträge im Archiv. 
                    <button 
                      onClick={() => setShowArchived(true)}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      Archiv anzeigen
                    </button>
                  </p>
                </div>
              ) : (
                <p>Keine Aufträge gefunden</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuftragsUebersicht;